/**
 * Verlauf in der URL: 16 Parameter (15 Filter und `view`). Auslöser, Ergebnis, Zustellung und Art
 * dürfen mehrere Werte tragen, kommagetrennt (`outcome=Change,Locked`); ein einzelner Wert bleibt
 * gültig wie bisher. Unbekannte oder kaputte Werte fallen weg, statt die Abfrage zu verfälschen.
 */
import type { DynamicPricingHistoryView } from '@/shared/api';

export interface DynamicPricingHistorySearch {
  view?: DynamicPricingHistoryView;
  apartment?: string;
  stayFrom?: string;
  stayTo?: string;
  decisionFrom?: string;
  decisionTo?: string;
  trigger?: string;
  outcome?: string;
  publicationStatus?: string;
  publicationCreatedFrom?: string;
  publicationCreatedTo?: string;
  exposureConfirmedFrom?: string;
  exposureConfirmedTo?: string;
  exposureKind?: string;
  profileVersion?: string;
  configurationVersion?: string;
}

export const historyViews = new Set<DynamicPricingHistoryView>([
  'decisions',
  'publications',
  'exposures',
]);

export function historyView(value: unknown): DynamicPricingHistoryView {
  return typeof value === 'string' && historyViews.has(value as DynamicPricingHistoryView)
    ? (value as DynamicPricingHistoryView)
    : 'publications';
}

export const TRIGGERS = [
  'BookingNew',
  'BookingModify',
  'BookingCancel',
  'BookingPaymentConfirmed',
  'Scheduled',
  'ConfigurationChanged',
];
export const OUTCOMES = ['Change', 'Skip', 'Unchanged', 'Target', 'Locked', 'Blocked'];
export const PUBLICATION_STATUSES = [
  'Queued',
  'SendStarted',
  'Sent',
  'Confirmed',
  'Failed',
  'ConfirmationFailed',
  'Superseded',
];
export const EXPOSURE_KINDS = ['Base', 'ChannelModifier'];
export const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Werte einer Liste in fester Reihenfolge, ohne Doppel; leer heißt „nicht gesetzt“. */
export function listValue(values: readonly string[], allowed: readonly string[]) {
  const picked = allowed.filter((value) => values.includes(value));
  return picked.length ? picked.join(',') : undefined;
}

export const listOf = (value: string | undefined) => (value ? value.split(',') : []);

export function parseDynamicPricingHistorySearch(
  raw: Record<string, unknown>,
): DynamicPricingHistorySearch {
  const parsed: DynamicPricingHistorySearch = {};
  const text = (key: string) => (typeof raw[key] === 'string' ? (raw[key] as string) : undefined);
  const single = (key: keyof DynamicPricingHistorySearch, valid: (value: string) => boolean) => {
    const value = text(key);
    if (value !== undefined && valid(value)) (parsed as Record<string, string>)[key] = value;
  };
  const list = (key: keyof DynamicPricingHistorySearch, allowed: readonly string[]) => {
    const value = text(key);
    // Leerzeichen und ein Komma am Ende sind Tippspuren, kein unbekannter Wert.
    const values =
      value
        ?.split(',')
        .map((entry) => entry.trim())
        .filter(Boolean) ?? [];
    // Ein Unbekannter verwirft die ganze Liste – sonst würde still enger gefiltert als verlangt.
    if (values.length && values.every((entry) => allowed.includes(entry))) {
      (parsed as Record<string, string | undefined>)[key] = listValue(values, allowed);
    }
  };
  single('view', (value) => historyViews.has(value as DynamicPricingHistoryView));
  single('apartment', (value) => GUID.test(value));
  single('stayFrom', validDate);
  single('stayTo', validDate);
  single('decisionFrom', validInstant);
  single('decisionTo', validInstant);
  list('trigger', TRIGGERS);
  list('outcome', OUTCOMES);
  list('publicationStatus', PUBLICATION_STATUSES);
  single('publicationCreatedFrom', validInstant);
  single('publicationCreatedTo', validInstant);
  single('exposureConfirmedFrom', validInstant);
  single('exposureConfirmedTo', validInstant);
  list('exposureKind', EXPOSURE_KINDS);
  single('profileVersion', (value) => GUID.test(value));
  single('configurationVersion', (value) => GUID.test(value));
  return parsed;
}

export function validDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const instant = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(instant.valueOf()) && instant.toISOString().startsWith(value);
}

export function validInstant(value: string) {
  return (
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,7})?)?Z$/.test(value) &&
    !Number.isNaN(Date.parse(value))
  );
}
